// ============================================================
// Sigil generation & data pools (first draft)
// ============================================================
/**
 * Pool of rule-bending special effects. Each sigil carries exactly one of these.
 * `id` is the hook the combat code will branch on later (phase 2 wiring).
 */
export const SIGIL_SPECIAL_POOL = [
    // ── Cast / ability shape
    { id: 'prism_of_splitting', name: 'Prism of Splitting', iconKey: 'sigil_1', description: 'A single-target spell also lashes a second foe for 30 % damage.' },
    { id: 'sigil_of_echo', name: 'Sigil of the Echo', iconKey: 'sigil_2', description: 'The last spell cast echoes 1 s later for 20 % damage.' },
    { id: 'hourglass_of_stasis', name: 'Hourglass of Stasis', iconKey: 'sigil_3', description: 'Spells return 25 % faster — but the precision to land critical strikes is gone.' },
    { id: 'gem_of_interrupt', name: 'Gem of Interrupt', iconKey: 'sigil_4', description: 'Every sixth strike rattles the target — stun for 0.6 s.' },
    // ── Combat rule-bending
    { id: 'vampires_locket', name: "Vampire's Locket", iconKey: 'sigil_5', description: 'When you drain life, the most wounded ally drinks too — 10 % of the amount.' },
    { id: 'thorn_of_reckoning', name: 'Thorn of Reckoning', iconKey: 'sigil_6', description: 'Hooks 25 % of incoming wounds back to the attacker. Your heroes no longer dodge.' },
    { id: 'bramble_totem', name: 'Bramble Totem', iconKey: 'sigil_7', description: 'Roots and stuns lash to nearby foes once. They do not chain further.' },
    { id: 'evil_pact', name: 'Evil Pact', iconKey: 'sigil_8', description: 'Burns and bleeds can land critical strikes — 10 % chance, doubled damage.' },
    // ── Economy / pacing
    { id: 'alchemists_scale', name: "Alchemist's Scale", iconKey: 'sigil_9', description: 'Every eighth fallen treasure rises one rarity higher.' },
    { id: 'merchants_coin', name: "Merchant's Coin", iconKey: 'sigil_10', description: 'The merchant restocks twice as quickly, but every price rises by 15 %.' },
    // ── Risk / reward
    { id: 'deaths_promise', name: "Death's Promise", iconKey: 'sigil_11', description: 'When any hero falls to a single breath, the party drinks back 50 % of their lifeblood. Twice per run.' },
    { id: 'bloody_ledger', name: 'Bloody Ledger', iconKey: 'sigil_12', description: 'Each wave begins at 15 % HP. The first ten strikes always land as critical hits.' },
    // ── Hero-defining
    { id: 'wolfs_mantle', name: "Wolf's Mantle", iconKey: 'sigil_13', description: 'Thorne wears the bear forever — Regrowth and Wild Growth are sealed away.' },
    // ── Quest-gated (unlocked by completing a StoryQuest — won't roll from random drops before then)
    {
        id: 'magmaraths_ember',
        // Reuse sigil_11 (red ember/flame glyph) — fits the fire/burn theme until a custom
        // sigil_14 sprite is added.
        name: "Magmarath's Ember",
        iconKey: 'sigil_11',
        description: 'Crits leave an ashen burn — 3 % of the target\'s max HP over 8 s. Always carries +5 % fire resist.',
        requiresQuestId: 'labyrinths_heart',
        baseAffixes: [{ key: 'fireResist', value: 5 }],
    },
    {
        // Telurion's Lair drop. Quest-gated — won't roll from random drops until the
        // player has cleared Telurion's Lair quest. The first lair-clear always
        // grants one via generateSpecificSigil regardless of unlock state, so the
        // player gets the sigil to play with before it joins the random pool.
        id: 'stormcoil_charm',
        // Reuse sigil_3 (blue lightning/ward glyph) — fits storm/nature theme.
        name: 'Stormcoil Charm',
        iconKey: 'sigil_3',
        description: 'A critical strike calls down lightning to one nearby foe for 60 % of the blow. Always carries +5 % nature resist.',
        requiresQuestId: 'telurions_lair',
        baseAffixes: [{ key: 'natureResist', value: 5 }],
    },
    {
        // Shadowlord first-kill drop. Sigil itself stays in the random-roll pool
        // but is gated behind the stage-50 quest so casual runs don't see it
        // before they've earned access. Reuses sigil_4 — purple/violet glyph
        // matches the void/shadow theme.
        id: 'shadowrealm_sigil',
        name: 'Shadowrealm Sigil',
        iconKey: 'sigil_4',
        description: 'Shadow damage taken is reduced. Killing an enemy in endless mode briefly bumps your party damage. Always carries +8 % shadow resist.',
        requiresQuestId: 'shadowrealm_breach',
        baseAffixes: [{ key: 'shadowResist', value: 8 }],
    },
];
export const SIGIL_AFFIX_POOL = [
    // Core stats — common, small rolls
    { key: 'atkPct', minValue: 1, maxValue: 5, weight: 12 },
    { key: 'spellPowerPct', minValue: 1, maxValue: 5, weight: 10 },
    { key: 'critChance', minValue: 1, maxValue: 3, weight: 10 },
    { key: 'critDmgPct', minValue: 5, maxValue: 15, weight: 8 },
    { key: 'attackSpeedPct', minValue: 1, maxValue: 5, weight: 8 },
    { key: 'defPct', minValue: 2, maxValue: 8, weight: 10 },
    { key: 'hpPct', minValue: 2, maxValue: 8, weight: 10 },
    { key: 'maxMpPct', minValue: 5, maxValue: 15, weight: 6 },
    // Resistances — uncommon, varied strengths
    { key: 'fireResist', minValue: 1, maxValue: 5, weight: 6 },
    { key: 'frostResist', minValue: 1, maxValue: 5, weight: 6 },
    { key: 'shadowResist', minValue: 1, maxValue: 5, weight: 6 },
    { key: 'natureResist', minValue: 1, maxValue: 5, weight: 6 },
    { key: 'allResist', minValue: 1, maxValue: 3, weight: 3 },
    // CC resistances — stubs for future content; lower weight
    { key: 'stunResist', minValue: 5, maxValue: 20, weight: 3 },
    { key: 'petrifyResist', minValue: 5, maxValue: 20, weight: 2 },
    { key: 'fearResist', minValue: 5, maxValue: 20, weight: 2 },
    // Regen
    { key: 'mpRegenPct', minValue: 5, maxValue: 25, weight: 6 },
    // Economy
    { key: 'goldPct', minValue: 5, maxValue: 15, weight: 8 },
    { key: 'lootPct', minValue: 2, maxValue: 8, weight: 5 },
    { key: 'xpPct', minValue: 3, maxValue: 10, weight: 5 },
    // Unique modifiers — rarer
    { key: 'ignoreArmorPct', minValue: 3, maxValue: 10, weight: 4 },
    // Monster-family damage boosts
    { key: 'vsBeastPct', minValue: 5, maxValue: 20, weight: 3 },
    { key: 'vsDemonPct', minValue: 5, maxValue: 20, weight: 3 },
    { key: 'vsUndeadPct', minValue: 5, maxValue: 20, weight: 3 },
    { key: 'vsOrcPct', minValue: 5, maxValue: 20, weight: 3 },
    { key: 'vsDragonPct', minValue: 5, maxValue: 20, weight: 2 },
    { key: 'vsMinotaurPct', minValue: 5, maxValue: 20, weight: 2 },
    { key: 'vsVoidPct', minValue: 5, maxValue: 20, weight: 2 },
    { key: 'vsBossPct', minValue: 3, maxValue: 12, weight: 3 },
];
/** Pick one weighted entry from the affix pool. */
function pickAffixTemplate() {
    const total = SIGIL_AFFIX_POOL.reduce((s, a) => s + a.weight, 0);
    let roll = Math.random() * total;
    for (const t of SIGIL_AFFIX_POOL) {
        roll -= t.weight;
        if (roll <= 0)
            return t;
    }
    return SIGIL_AFFIX_POOL[0];
}
function rollValue(t) {
    const lo = Math.min(t.minValue, t.maxValue);
    const hi = Math.max(t.minValue, t.maxValue);
    return Math.round(lo + Math.random() * (hi - lo));
}
function pickUniqueAffixes(count, exclude = new Set()) {
    const seen = new Set(exclude);
    const out = [];
    // Give up after N tries if the pool is exhausted (shouldn't happen with current pool).
    const MAX_TRIES = 50;
    for (let tries = 0; out.length < count && tries < MAX_TRIES; tries++) {
        const tpl = pickAffixTemplate();
        if (seen.has(tpl.key))
            continue;
        seen.add(tpl.key);
        out.push({ key: tpl.key, value: rollValue(tpl) });
    }
    return out;
}
function defaultUnlockCheck(special) {
    return !special.requiresQuestId;
}
/** Build the full affix list for a sigil instance: any base affixes the special always carries
 *  (e.g. Magmarath's Ember +5 % fire resist) plus `rolledCount` random affixes that cannot
 *  duplicate a base affix's key. */
function buildAffixes(special, rolledCount) {
    const base = (special.baseAffixes ?? []).map((a) => ({ ...a }));
    const excludeKeys = new Set(base.map((a) => a.key));
    return [...base, ...pickUniqueAffixes(rolledCount, excludeKeys)];
}
function newSigil(special, affixes) {
    const now = Date.now();
    return {
        id: `sigil_${now}_${Math.random().toString(36).slice(2, 7)}`,
        name: special.name,
        special,
        affixes,
        acquiredAt: now,
    };
}
/**
 * Roll a new sigil: random special effect + 1 or 2 random affixes (50/50), plus any base
 * affixes the special always carries. Quest-gated specials (e.g. story rewards) are excluded
 * from the random pool when their `requiresQuestId` is still locked — pass an `isUnlocked`
 * predicate to evaluate per-quest unlock state. Omitting the predicate treats every gated
 * sigil as locked, so callers that haven't wired the save data can't accidentally leak them.
 */
export function generateSigil(isUnlocked = defaultUnlockCheck) {
    const pool = SIGIL_SPECIAL_POOL.filter(isUnlocked);
    // Fall back to the unfiltered pool in the pathological "everything is locked" case so we
    // never return undefined. This only matters for tests and misconfigured saves.
    const effective = pool.length > 0 ? pool : SIGIL_SPECIAL_POOL;
    const special = effective[Math.floor(Math.random() * effective.length)];
    const rolledCount = Math.random() < 0.5 ? 1 : 2;
    return newSigil(special, buildAffixes(special, rolledCount));
}
/** Grant a specific sigil by its special-id (used for story-quest rewards). Always produces
 *  one instance regardless of quest-gating — the caller is responsible for deciding it's
 *  appropriate to hand out. `rolledCount` defaults to 1 random affix (in addition to any
 *  base affixes). Returns null if the special id is unknown. */
export function generateSpecificSigil(specialId, rolledCount = 1) {
    const special = SIGIL_SPECIAL_POOL.find((s) => s.id === specialId);
    if (!special)
        return null;
    return newSigil(special, buildAffixes(special, rolledCount));
}
/** Human-readable label for a single affix — used in tooltips.
 *  Format: `+<value> <short label> %` (all affix values are percentages). */
export function formatAffix(a) {
    const labelMap = {
        atkPct: 'ATK',
        spellPowerPct: 'SP',
        critChance: 'Crit',
        critDmgPct: 'Crit DMG',
        attackSpeedPct: 'Atk Spd',
        defPct: 'DEF',
        hpPct: 'HP',
        maxMpPct: 'Mana',
        fireResist: 'Fire Res',
        frostResist: 'Frost Res',
        shadowResist: 'Shadow Res',
        natureResist: 'Nature Res',
        allResist: 'All Res',
        stunResist: 'Stun Res',
        petrifyResist: 'Petrify Res',
        fearResist: 'Fear Res',
        mpRegenPct: 'Mana Regen',
        goldPct: 'Gold',
        lootPct: 'Loot',
        xpPct: 'XP',
        ignoreArmorPct: 'Ignore Armor',
        vsBeastPct: 'vs Beasts',
        vsDemonPct: 'vs Demons',
        vsUndeadPct: 'vs Undead',
        vsOrcPct: 'vs Orcs',
        vsDragonPct: 'vs Dragons',
        vsMinotaurPct: 'vs Minotaurs',
        vsVoidPct: 'vs Void',
        vsBossPct: 'vs Bosses',
    };
    return `+${a.value} ${labelMap[a.key]} %`;
}
//# sourceMappingURL=sigils.js.map